import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );

    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Returns a clean product URL for Quick Add fetching.
   *
   * Recommendation and collection tracking parameters are useful
   * for normal navigation but unnecessary for fetching the product
   * markup used by the Quick Add modal.
   *
   * @param {string} productPageUrl
   * @returns {string}
   */
  #getQuickAddFetchUrl(productPageUrl) {
    if (!productPageUrl) return '';

    try {
      const url = new URL(productPageUrl, window.location.origin);

      /*
       * Keep only the actual product path.
       *
       * Example:
       * /products/product-name?pr_rec_id=...
       * becomes:
       * /products/product-name
       */
      url.search = '';
      url.hash = '';

      return url.toString();
    } catch (error) {
      console.warn(
        '[quick-add] Could not normalize product URL:',
        productPageUrl,
        error
      );

      return productPageUrl;
    }
  }

  /**
   * Gets the currently selected variant ID from the product card.
   *
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();

    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  /**
   * Updates quick-add button state when product variant is selected.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    ) {
      return;
    }

    if (this.dataset.usesSellingPlans === 'true') return;

    /*
     * Only flip choose <-> add when both buttons were rendered.
     */
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount =
      this.dataset.productOptionsCount;

    let quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    if (
      quickAddButton === 'add' &&
      this.#isSelectedVariantUnavailable()
    ) {
      quickAddButton = 'choose';
    }

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  /**
   * Whether the currently selected variant is unavailable.
   *
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    return (
      productCard?.variantPicker?.selectedOption?.dataset
        .optionAvailable === 'false'
    );
  }

  /**
   * Clears cached modal content after cart updates.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the Quick Add modal.
   *
   * @param {Element} newHtml
   */
  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector(
          'variant-picker'
        )
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(
      newHtml
    );
  }

  /**
   * Handles Quick Add button click.
   *
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const navigationUrl =
      this.productPageUrl;

    if (!navigationUrl) {
      console.warn(
        '[quick-add] No product URL was available.'
      );

      return;
    }

    if (
      this.dataset.usesSellingPlans === 'true'
    ) {
      window.location.href =
        navigationUrl;

      return;
    }

    /*
     * Use a clean URL only for fetching Quick Add HTML.
     * Keep the original URL for normal product navigation.
     */
    const fetchUrl =
      this.#getQuickAddFetchUrl(
        navigationUrl
      );

    let productGrid =
      this.#cachedContent.get(fetchUrl);

    if (!productGrid) {
      const html =
        await this.fetchProductPage(
          fetchUrl
        );

      if (html) {
        const gridElement =
          html.querySelector(
            '[data-product-grid-content]'
          );

        if (gridElement) {
          productGrid =
            /** @type {Element} */ (
              gridElement.cloneNode(true)
            );

          this.#cachedContent.set(
            fetchUrl,
            productGrid
          );
        } else {
          console.warn(
            '[quick-add] Product page loaded but product grid was not found:',
            fetchUrl
          );
        }
      }
    }

    /*
     * Safe fallback.
     *
     * If Safari or Shopify refuses the modal fetch,
     * send the customer normally to the product page.
     */
    if (!productGrid) {
      window.location.href =
        navigationUrl;

      return;
    }

    const freshContent =
      /** @type {Element} */ (
        productGrid.cloneNode(true)
      );

    await this.updateQuickAddModal(
      freshContent
    );

    this.#updateVariantPicker(
      productGrid
    );

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute(
          'stay-visible',
          false
        );
      },
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (
      !(dialogComponent instanceof QuickAddDialog)
    ) {
      return;
    }

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content for Quick Add.
   *
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    const controller =
      new AbortController();

    this.#abortController =
      controller;

    try {
      const response =
        await fetch(
          productPageUrl,
          {
            method: 'GET',
            signal: controller.signal,
            credentials: 'same-origin',
            cache: 'no-store',
            redirect: 'follow',
            headers: {
              Accept: 'text/html',
            },
          }
        );

      if (!response.ok) {
        console.warn(
          `[quick-add] Product request failed with HTTP ${response.status}:`,
          productPageUrl
        );

        return null;
      }

      const responseText =
        await response.text();

      if (!responseText) {
        console.warn(
          '[quick-add] Product request returned an empty response:',
          productPageUrl
        );

        return null;
      }

      return new DOMParser()
        .parseFromString(
          responseText,
          'text/html'
        );
    } catch (error) {
      /*
       * Normal cancellation.
       */
      if (
        error?.name === 'AbortError'
      ) {
        return null;
      }

      /*
       * Safari can surface network failures as:
       * TypeError: Load failed
       *
       * Keep it controlled rather than generating
       * an Unhandled Promise Rejection.
       */
      console.warn(
        '[quick-add] Unable to load product page:',
        productPageUrl,
        error
      );

      return null;
    } finally {
      if (
        this.#abortController ===
        controller
      ) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the Quick Add content.
   *
   * @param {Element} productGrid
   */
  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (
      !productGrid ||
      !modalContent
    ) {
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(
      modalContent,
      productGrid
    );

    this.#syncVariantSelection(
      modalContent
    );
  }

  /**
   * Syncs variant selection from card to modal.
   *
   * @param {Element} modalContent
   */
  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true,
            }
          )
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}


class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } =
          detail;

        const anchorElement =
          /** @type {HTMLAnchorElement} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        if (!anchorElement) return;

        if (viewMoreDetailsLink) {
          viewMoreDetailsLink.href =
            anchorElement.href;
        }

        if (mobileProductTitle) {
          mobileProductTitle.href =
            anchorElement.href;
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (grid) {
        const currentWidth =
          grid.getBoundingClientRect().width;

        grid.style.width =
          `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}